import { computed, reactive } from "vue";
import oliverClient from "@/lib/OliverClient";
import {
  ATTACHMENT_REFERENCE,
  MARKDOWN_IMAGE,
  attachmentIdFor,
  bareAsNodes,
} from "./references.js";
import { isTableMime, parseTable } from "./tableCard.js";

/**
 * Resolved URL -> attachment id, module-scoped so a rendered element can be traced
 * back to the artifact it came from without changing how it is rendered.
 *
 * A resolved URL is a presigned one and expires. Reload is already safe — the URL
 * is minted on read — but a conversation left open past the expiry has a dead
 * `src` in the DOM, which is the case this map exists to repair.
 */
const ARTIFACT_IDS = new Map();

/** Image load failures an artifact may have before it is shown as unavailable. */
const ARTIFACT_REFRESH_MAX_ATTEMPTS = 3;

/**
 * The files one answer references, its charts and tables, from the reference to what is on
 * screen: resolving each to a URL, downloading charts before they are shown, fetching table
 * rows once, and re-signing a URL that expired while the answer sat open.
 *
 * `message` is a getter for the message prop. Its attachments and artifacts are read through
 * computeds, so a new message object with the same ones, which every activity update makes,
 * does not re-render the text.
 */
export function useAnswerArtifacts(message) {
  const attachments = computed(() => message().attachments);
  const artifacts = computed(() => message().artifacts);

  const state = reactive({
    // Charts referenced by an answer that is still streaming, id -> url. The
    // message's own attachments arrive with the saved message, which is after the
    // last token; without this a chart would only appear once the turn ended.
    resolvedAttachments: {},
    // Artifact URLs whose image has finished downloading. Reactive, so the
    // skeleton is swapped for the real chart the moment it is ready — and it
    // survives the re-renders that a DOM-held flag could not.
    loadedImages: {},
    // Image URLs that failed with no artifact behind them to re-mint.
    failedImages: {},
    // Artifacts the reader has already retried, so a second failure says what to do instead.
    retriedAttachments: {},
    // Rows of the tables an answer references, id -> parsed table or { failed }.
    // Fetched once per attachment, like the charts, never per streamed token.
    tables: {},
  });

  const fetching = new Set();
  const loadingTables = new Set();
  const preloading = new Set();
  const refreshing = new Set();
  const refreshAttempts = {};

  const idFor = (reference) => attachmentIdFor(reference, artifacts.value);

  /**
   * Whether a URL is one of Oliver's own files: resolved from an attachment
   * reference, or one of this message's attachments. The only images an answer loads.
   */
  function isOliverFile(url) {
    return (
      ARTIFACT_IDS.has(url) ||
      (attachments.value || []).some((att) => att?.url && att.url === url)
    );
  }

  /**
   * Start fetching every image the content points at.
   *
   * Driven from here rather than from the render, so the request is made once
   * per URL and owned by this component. The <img> in the markup is rebuilt on
   * every streamed token — `v-html` replaces the whole subtree — so an element
   * that has to download a few hundred kilobytes never survives long enough to
   * finish, and the chart only appears once the answer stops streaming. This
   * download does survive, and the markup is given the <img> after it lands.
   */
  function preloadContentImages(content) {
    for (const [, , url] of content.matchAll(MARKDOWN_IMAGE)) {
      if (!isOliverFile(url)) continue;
      preloadArtifactImage(url, ARTIFACT_IDS.get(url) || null);
    }
  }

  /**
   * Give every attachment reference in the content something to render with.
   *
   * Stores the MIME type, filename and size alongside the URL, because they decide whether the
   * reference becomes an image, a table or a file card — a URL alone cannot tell you.
   *
   * Prefers the message's own attachments, which carry a freshly signed URL and cost
   * nothing. Anything still unresolved is fetched once — that is the streaming case, where
   * the file exists but the saved message does not yet.
   */
  async function resolveAttachments(content) {
    const ids = new Set();
    for (const [, , , ref] of bareAsNodes(content).matchAll(
      ATTACHMENT_REFERENCE
    )) {
      const id = idFor(ref);
      if (id && !state.resolvedAttachments[id]) ids.add(id);
    }
    if (!ids.size) return;

    for (const att of message().attachments || []) {
      if (att?.id && att.url && ids.has(att.id)) {
        rememberAttachment(att.id, {
          url: att.url,
          mimeType: att.mime_type || "",
          filename: att.original_file_name || "",
          size: att.size || null,
        });
        ids.delete(att.id);
      }
    }

    const pending = [...ids].filter((id) => !fetching.has(id));
    pending.forEach((id) => fetching.add(id));

    await Promise.all(
      pending.map(async (id) => {
        try {
          const { data } = await oliverClient.getAttachment(id);
          if (data?.url) {
            rememberAttachment(id, {
              url: data.url,
              mimeType: data.mime_type || "",
              filename: data.original_file_name || "",
              size: data.size || null,
            });
          } else {
            // 200 with no url (backend's own retries were exhausted) — mark failed, not a forever-spinner.
            state.resolvedAttachments[id] = { failed: true };
          }
        } catch (error) {
          state.resolvedAttachments[id] = { failed: true };
          console.error("Attachment fetch failed", id, error);
        } finally {
          // Every outcome above is recorded, so this never re-fetches by itself; Retry can.
          fetching.delete(id);
        }
      })
    );
  }

  function rememberAttachment(id, resolved) {
    state.resolvedAttachments[id] = resolved;
    if (resolved.url) ARTIFACT_IDS.set(resolved.url, id);
    if ((resolved.mimeType || "").startsWith("image/")) {
      preloadArtifactImage(resolved.url, id);
    }
    if (isTableMime(resolved.mimeType)) loadTable(id);
  }

  /**
   * Fetch a table's rows once. A table that cannot be read stays the plain download
   * link it always was, never a broken card.
   */
  async function loadTable(id) {
    if (state.tables[id] || loadingTables.has(id)) return;
    loadingTables.add(id);
    try {
      const { data } = await oliverClient.getAttachmentTable(id);
      state.tables[id] = parseTable(data) || { failed: true };
    } catch (error) {
      state.tables[id] = { failed: true };
      console.error("Attachment table fetch failed", id, error);
    } finally {
      loadingTables.delete(id);
    }
  }

  /**
   * Download a chart before it is ever put in the document.
   *
   * The markdown is injected with `v-html`, which replaces the whole subtree
   * on every streamed token — so an <img> in it is destroyed and recreated
   * ten times a second, and anything that depends on its `load` event is
   * racing a element that keeps being thrown away. Here the request belongs
   * to an Image the component owns, so it completes once, and the markup only
   * gets the <img> when the file is already decoded and in cache.
   */
  function preloadArtifactImage(url, id) {
    if (!url || state.loadedImages[url] || state.failedImages[url]) return;
    if (preloading.has(url)) return;
    preloading.add(url);

    const image = new Image();
    image.onload = () => {
      state.loadedImages[url] = true;
      if (id) refreshAttempts[id] = 0;
    };
    image.onerror = () => {
      preloading.delete(url);
      if (id) refreshArtifact(id, url);
      else state.failedImages[url] = true;
    };
    image.src = url;
  }

  /** One attachment row, freshly signed. Null if it cannot be fetched. */
  async function fetchArtifact(id) {
    try {
      const { data } = await oliverClient.getAttachment(id);
      return data?.url ? data : null;
    } catch (error) {
      console.error("Attachment fetch failed", id, error);
      return null;
    }
  }

  /**
   * A freshly signed URL for an artifact, without disturbing what is on
   * screen.
   *
   * Deliberately does NOT write to `resolvedAttachments`: the rendered
   * markdown is computed from it, and an image is treated as loaded by URL —
   * so storing a new one swaps a chart that is sitting there perfectly well
   * back to a skeleton while it downloads all over again.
   */
  async function mintArtifactUrl(id) {
    const data = await fetchArtifact(id);
    return data?.url || null;
  }

  /**
   * Replace an artifact's URL because the one on screen no longer works.
   *
   * The new URL goes into `resolvedAttachments`, so the element repairs itself
   * on the next render and nothing here touches the DOM. Only for a URL that
   * has actually failed — re-downloading is the point, and the skeleton
   * reappearing is honest.
   *
   * Only a failure of the URL currently in use counts, one refresh at a time,
   * and after `ARTIFACT_REFRESH_MAX_ATTEMPTS` failures the artifact is shown as
   * unavailable. Only an image that loads clears the count: a URL can be
   * minted fine while the file behind it stays unreachable — and re-signing
   * within the same second returns the very URL that just failed.
   */
  async function refreshArtifact(id, failedUrl) {
    if (refreshing.has(id)) return;
    if (state.resolvedAttachments[id]?.url !== failedUrl) return;

    const attempts = (refreshAttempts[id] || 0) + 1;
    refreshAttempts[id] = attempts;

    refreshing.add(id);
    const data =
      attempts < ARTIFACT_REFRESH_MAX_ATTEMPTS ? await fetchArtifact(id) : null;
    refreshing.delete(id);
    if (!data) {
      state.resolvedAttachments[id] = { failed: true };
      return;
    }
    rememberAttachment(id, {
      url: data.url,
      mimeType: data.mime_type || "",
      filename: data.original_file_name || "",
      size: data.size || null,
    });
  }

  /** An artifact whose link cannot be minted any more: shown as failed, with Retry. */
  function markUnavailable(id) {
    state.resolvedAttachments[id] = { failed: true };
  }

  /** Resolve a failed artifact again from scratch, with a fresh set of attempts. */
  function retryArtifact(id, content) {
    state.retriedAttachments[id] = true;
    refreshAttempts[id] = 0;
    delete state.resolvedAttachments[id];
    resolveAttachments(content);
  }

  /**
   * An artifact image that failed to load. Almost always an expired presigned
   * URL on a conversation that has been open a while, so it is re-minted
   * rather than left as a broken-image icon in the middle of an answer.
   *
   * Capture phase: `error` does not bubble.
   */
  function handleMediaError(event) {
    const el = event.target;
    if (!el || el.tagName !== "IMG") return;
    const src = el.getAttribute("src");
    const url = ARTIFACT_IDS.has(src) ? src : el.src;
    const id = ARTIFACT_IDS.get(url);
    if (id) refreshArtifact(id, url);
  }

  return {
    state,
    attachments: () => attachments.value,
    attachmentIdFor: idFor,
    artifactIdFor: (url) => ARTIFACT_IDS.get(url) || null,
    isOliverFile,
    preloadContentImages,
    resolveAttachments,
    retryArtifact,
    handleMediaError,
    markUnavailable,
    mintArtifactUrl,
  };
}
